import { useState } from "react";
import axios from "axios";
import {
  Mail,
  Phone,
  MessageCircle,
  Calendar,
  Loader2,
  CheckCircle2,
  ArrowLeft,
  ArrowRight,
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// When REACT_APP_CALENDLY_URL is set in /app/frontend/.env we open Calendly
// directly. Otherwise the "Reservar una llamada" tile smooth-scrolls to the
// multi-step form below, so the experience never breaks.
const CALENDLY_URL = process.env.REACT_APP_CALENDLY_URL || "";
const HAS_CALENDLY = Boolean(CALENDLY_URL);

const SIZE_OPTIONS = [
  { value: "<100", label: "Menos de 100 m²" },
  { value: "100-150", label: "Entre 100 y 150 m²" },
  { value: ">150", label: "Más de 150 m²" },
];

const MOTIVATION_OPTIONS = [
  { value: "no_gestion", label: "No quiero ocuparme de la gestión diaria" },
  { value: "problemas", label: "Quiero evitar problemas con los inquilinos" },
  { value: "ingresos", label: "Me interesa optimizar los ingresos del piso" },
  { value: "impago", label: "Me preocupa el impago o los retrasos" },
  { value: "otro", label: "Otro motivo" },
];

const TIMING_OPTIONS = [
  { value: "ahora", label: "Inmediatamente" },
  { value: "1_3_meses", label: "En 1 a 3 meses" },
  { value: "explorando", label: "Solo estoy explorando opciones" },
];

const TOTAL_STEPS = 5;

const initialForm = {
  name: "",
  email: "",
  phone: "",
  city: "",
  property_type: "",
  audience: "owner",
  size: "",
  motivation: "",
  timing: "",
  message: "",
};

export default function Contact() {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k) => (e) =>
    setForm((p) => ({ ...p, [k]: e?.target ? e.target.value : e }));
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const next = () => {
    if (step === 1) {
      if (!form.name || form.name.trim().length < 2)
        return toast.error("Introduce tu nombre completo.");
      if (!form.email || !/^\S+@\S+\.\S+$/.test(form.email))
        return toast.error("Introduce un email válido.");
      if (!form.phone || form.phone.trim().length < 6)
        return toast.error("Introduce un teléfono válido.");
    }
    if (step === 2 && !form.city.trim())
      return toast.error("Indica la ciudad de tu propiedad.");
    if (step === 3 && !form.size)
      return toast.error("Selecciona el tamaño del piso.");
    if (step === 4 && !form.motivation)
      return toast.error("Selecciona tu motivo principal.");
    if (step < TOTAL_STEPS) setStep((s) => s + 1);
  };

  const back = () => setStep((s) => Math.max(1, s - 1));

  const submit = async () => {
    if (!form.timing) {
      toast.error("Indica cuándo te gustaría empezar.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSubmitted(true);
      toast.success("Solicitud enviada. Te contactamos en menos de 24h.");
    } catch (err) {
      toast.error("No se pudo enviar. Inténtalo de nuevo en unos minutos.");
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setForm(initialForm);
    setStep(1);
    setSubmitted(false);
  };

  const progress = (step / TOTAL_STEPS) * 100;

  return (
    <section
      id="contacto"
      data-testid="contact-section"
      className="nr-section nr-bg-soft"
    >
      <div className="nr-container grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <span className="nr-eyebrow">Contacto</span>
          <h2 className="nr-h2 mt-4">Reserva tu llamada gratuita.</h2>
          <p className="mt-5 text-[16px] nr-text-soft max-w-md">
            Te contactamos en menos de 24 horas. Sin compromiso. Una llamada
            clara para entender tu vivienda y contarte cuánto puedes cobrar
            cada mes con THE NEXT ROOM.
          </p>

          <div className="mt-10 space-y-5">
            <a
              href={HAS_CALENDLY ? CALENDLY_URL : "#contact-form-card"}
              target={HAS_CALENDLY ? "_blank" : undefined}
              rel={HAS_CALENDLY ? "noreferrer" : undefined}
              data-testid="contact-calendly"
              className="flex items-center gap-4 p-5 rounded-2xl border border-[#EAEAEA] bg-white hover:border-[#43A4A8] transition-colors group"
            >
              <div className="nr-service-icon !mb-0 !w-12 !h-12 !rounded-xl flex-shrink-0">
                <Calendar size={20} />
              </div>
              <div className="flex-1">
                <div className="font-sub text-[15px]">
                  Reservar una llamada
                </div>
                <div className="text-[13px] nr-text-soft">
                  {HAS_CALENDLY
                    ? "Calendario online · 20 minutos · Sin compromiso"
                    : "Rellena el formulario · Te contactamos en 24h"}
                </div>
              </div>
              <span className="font-sub text-[13px] text-[#2E8E92] group-hover:translate-x-1 transition-transform">
                →
              </span>
            </a>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <a
                href="mailto:hola@thenextroom.es"
                className="flex items-center gap-3 p-4 rounded-xl border border-[#EAEAEA] bg-white hover:border-[#111] transition-colors"
                data-testid="contact-email"
              >
                <Mail size={18} className="text-[#2E8E92]" />
                <div className="text-[13px] font-sub">Email</div>
              </a>
              <a
                href="tel:+34681001182"
                className="flex items-center gap-3 p-4 rounded-xl border border-[#EAEAEA] bg-white hover:border-[#111] transition-colors"
                data-testid="contact-phone"
              >
                <Phone size={18} className="text-[#2E8E92]" />
                <div className="text-[13px] font-sub">Teléfono</div>
              </a>
              <a
                href="https://wa.me/34681001182"
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-3 p-4 rounded-xl border border-[#EAEAEA] bg-white hover:border-[#111] transition-colors"
                data-testid="contact-whatsapp"
              >
                <MessageCircle size={18} className="text-[#2E8E92]" />
                <div className="text-[13px] font-sub">WhatsApp</div>
              </a>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7" id="contact-form-card">
          <div className="bg-white border border-[#EAEAEA] rounded-3xl p-7 md:p-10">
            {submitted ? (
              <SuccessState onReset={reset} />
            ) : (
              <>
                <Stepper step={step} progress={progress} />
                <form
                  data-testid="contact-form"
                  className="mt-8"
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (step < TOTAL_STEPS) next();
                    else submit();
                  }}
                >
                  {step === 1 && (
                    <StepOne form={form} update={update} set={set} />
                  )}
                  {step === 2 && <StepTwo form={form} update={update} />}
                  {step === 3 && <StepThree form={form} set={set} />}
                  {step === 4 && <StepFour form={form} set={set} />}
                  {step === 5 && (
                    <StepFive form={form} set={set} update={update} />
                  )}

                  <div className="mt-9 flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-between gap-3">
                    {step > 1 ? (
                      <button
                        type="button"
                        onClick={back}
                        data-testid="step-back"
                        className="nr-btn nr-btn-ghost"
                      >
                        <ArrowLeft size={16} /> Volver
                      </button>
                    ) : (
                      <span className="hidden sm:block" />
                    )}

                    {step < TOTAL_STEPS ? (
                      <button
                        type="button"
                        onClick={next}
                        data-testid="step-next"
                        className="nr-btn nr-btn-primary"
                      >
                        Continuar <ArrowRight size={16} />
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={submit}
                        disabled={loading}
                        data-testid="contact-submit"
                        className="nr-btn nr-btn-primary disabled:opacity-60"
                      >
                        {loading ? (
                          <>
                            <Loader2 size={16} className="animate-spin" />{" "}
                            Enviando…
                          </>
                        ) : (
                          <>
                            Reservar llamada gratuita <ArrowRight size={16} />
                          </>
                        )}
                      </button>
                    )}
                  </div>

                  <p className="mt-6 text-[12px] nr-text-soft">
                    Datos protegidos. Solo los usaremos para contactarte. Sin
                    compromiso.
                  </p>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------------------- Sub components ---------------------- */

function Stepper({ step, progress }) {
  return (
    <div data-testid="contact-stepper">
      <div className="flex items-center justify-between text-[12px] font-sub">
        <span className="nr-step-num">
          Paso {String(step).padStart(2, "0")} de {TOTAL_STEPS}
        </span>
        <span className="nr-text-soft">
          {Math.round(progress)}% completado
        </span>
      </div>
      <div className="mt-3 h-1.5 rounded-full bg-[#EAEAEA] overflow-hidden">
        <div
          className="h-full bg-[#43A4A8] transition-all duration-500"
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  );
}

function AudiencePills({ form, set }) {
  const opts = [
    { value: "owner", label: "Propietario" },
    { value: "agency", label: "Inmobiliaria" },
    { value: "investor", label: "Inversor" },
  ];
  return (
    <div className="flex flex-wrap gap-2 mb-6">
      {opts.map((a) => (
        <button
          key={a.value}
          type="button"
          data-testid={`audience-${a.value}`}
          onClick={() => set("audience", a.value)}
          className={`px-4 py-2 rounded-full text-[13px] font-sub border transition-colors ${
            form.audience === a.value
              ? "bg-[#111] text-white border-[#111]"
              : "bg-white text-[#111] border-[#EAEAEA] hover:border-[#111]"
          }`}
        >
          Soy {a.label.toLowerCase()}
        </button>
      ))}
    </div>
  );
}

function FieldLabel({ children }) {
  return (
    <label className="block text-[13px] font-sub mb-1.5">{children}</label>
  );
}

function StepHeading({ title, subtitle }) {
  return (
    <div className="mb-7">
      <h3 className="nr-h3 text-[20px] md:text-[22px]">{title}</h3>
      {subtitle && (
        <p className="mt-2 text-[14px] nr-text-soft">{subtitle}</p>
      )}
    </div>
  );
}

function StepOne({ form, update, set }) {
  return (
    <div className="nr-fade-up">
      <AudiencePills form={form} set={set} />
      <StepHeading
        title="Cuéntanos quién eres"
        subtitle="Te contactamos en menos de 24 horas. Sin compromiso."
      />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="sm:col-span-2">
          <FieldLabel>Nombre completo *</FieldLabel>
          <input
            data-testid="input-name"
            type="text"
            required
            className="nr-input"
            value={form.name}
            onChange={update("name")}
            placeholder="Ej. María González"
          />
        </div>
        <div>
          <FieldLabel>Teléfono *</FieldLabel>
          <input
            data-testid="input-phone"
            type="tel"
            required
            className="nr-input"
            value={form.phone}
            onChange={update("phone")}
            placeholder="+34 600 000 000"
          />
        </div>
        <div>
          <FieldLabel>Email *</FieldLabel>
          <input
            data-testid="input-email"
            type="email"
            required
            className="nr-input"
            value={form.email}
            onChange={update("email")}
            placeholder="tu@email.com"
          />
        </div>
      </div>
    </div>
  );
}

function StepTwo({ form, update }) {
  return (
    <div className="nr-fade-up">
      <StepHeading
        title="¿En qué ciudad está tu propiedad?"
        subtitle="Cuéntanos la ciudad y la zona donde se encuentra la vivienda."
      />
      <FieldLabel>Ciudad / zona *</FieldLabel>
      <input
        data-testid="input-city"
        type="text"
        required
        className="nr-input"
        value={form.city}
        onChange={update("city")}
        placeholder="Ciudad y barrio (Ej. Madrid · Chamberí)"
      />
      <div className="mt-4">
        <FieldLabel>Tipo de vivienda (opcional)</FieldLabel>
        <input
          data-testid="input-property"
          type="text"
          className="nr-input"
          value={form.property_type}
          onChange={update("property_type")}
          placeholder="Ej. Piso de 3 habitaciones en Chamberí"
        />
      </div>
    </div>
  );
}

function StepThree({ form, set }) {
  return (
    <div className="nr-fade-up">
      <StepHeading
        title="¿Cuál es el tamaño aproximado del piso?"
        subtitle="Necesitamos viviendas de cierto tamaño para que el modelo funcione mejor."
      />
      <div className="grid grid-cols-1 gap-3">
        {SIZE_OPTIONS.map((o) => (
          <button
            type="button"
            key={o.value}
            data-testid={`size-${o.value}`}
            onClick={() => set("size", o.value)}
            className={`text-left px-5 py-4 rounded-xl border transition-all ${
              form.size === o.value
                ? "border-[#43A4A8] bg-[#43A4A8]/[0.06]"
                : "border-[#EAEAEA] bg-white hover:border-[#111]"
            }`}
          >
            <div className="font-sub text-[15px]">{o.label}</div>
          </button>
        ))}
      </div>
    </div>
  );
}

function StepFour({ form, set }) {
  return (
    <div className="nr-fade-up">
      <StepHeading
        title="¿Cuál es tu motivo principal?"
        subtitle="Cuanto más concreto, mejor podemos ayudarte."
      />
      <div className="grid grid-cols-1 gap-3">
        {MOTIVATION_OPTIONS.map((o) => (
          <button
            type="button"
            key={o.value}
            data-testid={`motivation-${o.value}`}
            onClick={() => set("motivation", o.value)}
            className={`text-left px-5 py-4 rounded-xl border transition-all ${
              form.motivation === o.value
                ? "border-[#43A4A8] bg-[#43A4A8]/[0.06]"
                : "border-[#EAEAEA] bg-white hover:border-[#111]"
            }`}
          >
            <div className="font-sub text-[15px]">{o.label}</div>
          </button>
        ))}
      </div>
    </div>
  );
}

function StepFive({ form, set, update }) {
  return (
    <div className="nr-fade-up">
      <StepHeading
        title="¿Cuándo te gustaría empezar?"
        subtitle="Última pregunta. Después te llamamos en menos de 24h."
      />
      <div className="grid grid-cols-1 gap-3">
        {TIMING_OPTIONS.map((o) => (
          <button
            type="button"
            key={o.value}
            data-testid={`timing-${o.value}`}
            onClick={() => set("timing", o.value)}
            className={`text-left px-5 py-4 rounded-xl border transition-all ${
              form.timing === o.value
                ? "border-[#43A4A8] bg-[#43A4A8]/[0.06]"
                : "border-[#EAEAEA] bg-white hover:border-[#111]"
            }`}
          >
            <div className="font-sub text-[15px]">{o.label}</div>
          </button>
        ))}
      </div>

      <div className="mt-5">
        <FieldLabel>Cuéntanos un poco más (opcional)</FieldLabel>
        <textarea
          data-testid="input-message"
          rows={4}
          className="nr-input resize-none"
          value={form.message}
          onChange={update("message")}
          placeholder="Cualquier detalle sobre tu vivienda o tu situación que nos ayude a prepararnos para la llamada…"
        />
      </div>
    </div>
  );
}

function SuccessState({ onReset }) {
  return (
    <div
      data-testid="contact-success"
      className="flex flex-col items-center text-center py-16"
    >
      <CheckCircle2 size={48} className="text-[#43A4A8]" />
      <h3 className="nr-h2 mt-6">¡Solicitud enviada!</h3>
      <p className="mt-3 nr-text-soft max-w-md">
        Nuestro equipo se pondrá en contacto contigo en menos de 24 horas.
        Gracias por confiar en THE NEXT ROOM.
      </p>
      <div className="mt-8 flex flex-col sm:flex-row gap-3">
        <a
          href="https://wa.me/34681001182"
          target="_blank"
          rel="noreferrer"
          className="nr-btn nr-btn-accent"
        >
          <MessageCircle size={16} /> Escríbenos por WhatsApp
        </a>
        <button
          type="button"
          onClick={onReset}
          className="nr-btn nr-btn-ghost"
        >
          Enviar otra solicitud
        </button>
      </div>
    </div>
  );
}
